export interface Impersonation {
  tenantId: string;
  token: string;
  email: string;
  label: string;
  returnTo: string;
}

/**
 * Reads admin "act as" parameters handed over from the Admin Portal via the URL
 * hash, e.g. "#act_as=barrow-cafe&act_as_token=...&label=Barrow%20Cafe". The
 * token is an API-minted, tenant-scoped act-as token (see
 * packages/auth/src/act-as.ts), not the admin's Supabase JWT.
 *
 * The hash is used (rather than the query string) so the bearer token is
 * never sent to a server; the caller strips it from the address bar immediately
 * after reading.
 */
export function readImpersonationParams(hash: string): Impersonation | null {
  if (typeof hash !== "string" || hash.length < 2) return null;

  const params = new URLSearchParams(hash.replace(/^#/, ""));
  const tenantId = params.get("act_as")?.trim() ?? "";
  const token = params.get("act_as_token")?.trim() ?? "";

  // Both the target tenant and the admin token are required for impersonation.
  if (!tenantId || !token) return null;

  return {
    tenantId,
    token,
    email: params.get("admin_email")?.trim() ?? "",
    label: params.get("label")?.trim() || tenantId,
    returnTo: params.get("return_to")?.trim() ?? "",
  };
}

// Per-tab so a refresh keeps the admin act-as session alive (the hash is stripped
// once consumed). Cleared on exit/sign-out and gone when the tab closes — it is
// never shared across tabs and never written to localStorage.
const STORAGE_KEY = "pgs.impersonation";

export function readStoredImpersonation(): Impersonation | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw);
    return value?.tenantId && value?.token ? value : null;
  } catch {
    return null;
  }
}

export function storeImpersonation(imp: Impersonation): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(imp));
  } catch {
    // Storage unavailable (private mode) — impersonation simply won't survive a refresh.
  }
}

export function clearStoredImpersonation(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}
